<template>
  <!------------------------------------------
    This component displays the web version of the congressional state profiles.
  -------------------------------------------->
  <div id="congr-district-web-v" class="lsc-dot-gov-styling">
    <div v-if="loading">
      <LoadingSpinner />
    </div>
    <Block v-else>
      <br/><br/>
      <div id="cd-profile-area-web" ref="profilearea" :class="$vuetify.breakpoint.smAndDown === true ? 'mobile' : ''" v-if="profileData !== undefined && profileData !== null">
        <!-- HEADER AREA -->
        <div class="grid-header">
          <h2 class="main-title">
            <span class="title-1">Civil legal needs & services in</span>
            <span class="title-2">{{ profileData.state }}'s {{ selectedCDLabel }}</span>
          </h2>
        </div>

        <div class="grid-download">
          <div id="one-pager-link">
            <v-icon
              color="#b31308"
              left
              class="one-pager-icon"
              size="30">
                {{ icons.download }}
            </v-icon>
            <a :href="onePagerBoxLinksByState[selectedCDFips.substring(0,2)]" target="_blank">Download the one-pager</a></div>
        </div>

        <div class="grid-needs grid-section">
          <h3 class="with-left-border">Legal Needs</h3>
          
          <!-- JUSTICE GAP STATS -->
          <div class="grid-section-row">
            <ProfileDataRow title="The Justice Gap" icon="scale">
              <div id="justice-gap-points">
                <div class="justice-gap-row">
                  <ProfileDataPoint :dataPoint="percentToOneInX(profileData.population.pct_eligible)" :text="'constituents in the district (' + formatThousandsNoDecimal(profileData.population.eligible) + ') are eligible for LSC services.'" :inlinePoint="true"/>
                  <ProfileDataPoint dataPoint="3 in 4" text="will face at least one civil legal problem this year." :inlinePoint="true"/>
                  <ProfileDataPoint :dataPoint="'Only ' + formatPercentNoDecimalIfExists(profileData.justice_gap.problems_pct_unhelped)" text="of their legal problems will receive enough legal help." :inlinePoint="true"/>
                </div>
              </div>
            </ProfileDataRow>
          </div>
          
          <!-- ELIGIBILITY & POVERTY -->
          <div class="grid-section-row">
            <ProfileDataRow title="Also LSC Eligible" icon="people" :centered="false" columnsWhenWide="3">
              <ProfileDataPoint icon="seniors" :dataPoint="percentToOneInX(profileData.population.seniors_eligible)" text="older Americans" :sideBySide="true"/>
              <ProfileDataPoint icon="child" :dataPoint="percentToOneInX(profileData.population.pct_children_in_elig_hh)" text="children" :sideBySide="true"/>
              <ProfileDataPoint v-if="profileData.population.vet_pov_pct > 0.067" icon="star" :dataPoint="percentToOneInX(profileData.population.vet_pov_pct)" text="veterans" :sideBySide="true"/>
            </ProfileDataRow>
          </div>
  
          <!-- HOUSING STATS -->
          <div class="grid-section-row">
            <ProfileDataRow title="Housing Issues" icon="house" columnsWhenWide="1">
              <ProfileDataPoint v-for="pnt in housingPoints" :key="pnt.name" icon="circle" :introText="pnt.introText" :sideBySide="pnt.sideBySide" :dataPoint="String(pnt.dataPoint)" :text="pnt.text" :comparisonDataPoint="pnt.comparisonDataPoint" :comparisonText="pnt.comparisonText" :afterText="pnt.afterText" :inlinePoint="pnt.inlinePoint" />
            </ProfileDataRow>
          </div>
        </div>

        <div class="grid-spacer" :class="'spacer-height-' + String(numGrantees)"></div>

        <!-- GRANTEES -->
        <div class="grid-grantees-header">
          <div>
            <h3 class="with-left-border">LSC Grantees</h3>
          </div>
          <!-- show state map thumbnail to the right of the title if multiple relevant grantees and wide-enough browser -->
          <div v-if="numGrantees > 1 && $vuetify.breakpoint.smAndDown === false && hideStateMap.indexOf(selectedCDFips) === -1">
            <CDProfilesPrintStateMap
              v-if="singleCDState === false"
              id="st-map"
              :key="selectedCDFips"
              :selectedCDFips="selectedCDFips"
              :interactive="false"
              mapboxStyle="mapbox://styles/lsc-odga/clah3hb8i000914m3b08kcyjd"
              :colorPerServArea="colorPerServArea"
              :officesPerGrantee="officesPerGrantee"
              mapHeight="12rem"
            />
          </div>
        </div>
        <div class="grid-grantees grid-section">
          <div id="grantees-list">
            <div class="grantees-list-g" :class="'grantee-count-' + String(numGrantees)" v-for="g in granteeDataPoints" :key="g.rno">
              <div class="grantee-name-row">
                <a :href="selectedOrgProfileLink(g.rno)" target="_parent">
                  <div
                    title="Click to learn more about this grantee" 
                    class="grantee-list-item-header"
                    :id="'list-item-header-' + g.rno" 
                  >
                    <v-icon v-if="colorPerServArea.find(x => x.rno === g.rno)"
                      :color="colorPerServArea.find(x => x.rno === g.rno).color"
                      class="grantee-color"
                      size="22">
                      {{ icons.circle }}
                    </v-icon>
                    <div class="grantee-name">
                      {{ g.data.org_name }}
                    </div>
                  </div>
                </a>
              </div>
              <div class="grantees-list-info">
                <div class="serves">
                  <v-icon
                    color="#5a5a5a"
                    left
                    class="grantees-list-icon"
                    size="20">
                      {{ icons.serves }}
                  </v-icon>
                  Serves: <span class="grantee-info-point">{{ formatPercentNoDecimalIfExists(g.data.percent_of_constituents) }}</span> of eligible constituents
                </div>
                <div class="offices-list">
                  <div v-if="officesShown !== null && Object.keys(officesShown).indexOf(g.rno) > -1">
                    <v-icon
                      color="#6a6a6a"
                      left
                      class="grantees-list-icon"
                      size="20">
                        {{ icons.main }}
                    </v-icon>
                    Offices shown<span v-if="hasOfficeInDistrict === true && singleCDState === false"> (*in the district)</span>:
                    <br v-if="hasOfficeInDistrict === true"/>
                    <span>
                      {{ officesShown[g.rno] }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <!-- show state thumbnail map below the grantees list if it is a single grantee on a wide-enough browser window-->
            <div v-if="$vuetify.breakpoint.smAndDown === false && numGrantees === 1 && hideStateMap.indexOf(selectedCDFips) === -1">
              <CDProfilesPrintStateMap
                v-if="singleCDState === false"
                id="st-map"
                :key="selectedCDFips"
                :selectedCDFips="selectedCDFips"
                :interactive="false"
                mapboxStyle="mapbox://styles/lsc-odga/clah3hb8i000914m3b08kcyjd"
                :colorPerServArea="colorPerServArea"
                :officesPerGrantee="officesPerGrantee"
                mapHeight="18rem"
              />
            </div>
          </div>
        </div>

        <!-- MAP -->
        <div class="grid-map grid-section">
          <CDProfilesWebMap
            :key="selectedCDFips"
            :selectedCDFips="selectedCDFips"
            :interactive="false"
            mapboxStyle="mapbox://styles/lsc-odga/clah3hb8i000914m3b08kcyjd"
            :colorPerServArea="colorPerServArea"
            :officesPerGrantee="officesPerGrantee"
            :stateName="profileData.state"
            @officesInView="parseOfficesList($event)"
          />
        </div>

        <div class="grid-spacer2" v-if="$vuetify.breakpoint.smAndDown === false"></div>

        <div class="grid-served grid-section">
          <h3 class="with-left-border">Services</h3>
          
          <!-- PEOPLE SERVED -->
          <div class="grid-section-row">
            <ProfileDataRow :title="'Constituents Served in ' + profileData.gar_year" icon="handshake" :centered="false" columnsWhenWide="2">
              <ProfileDataPoint :dataPoint="formatThousandsNoDecimal(profileData.served.people)" icon="people" text="people" :sideBySide="true" />
              <ProfileDataPoint  :dataPoint="formatThousandsNoDecimal(profileData.served.seniors)" icon="seniors" text="older Americans" :sideBySide="true" />
              <ProfileDataPoint :dataPoint="formatThousandsNoDecimal(profileData.served.veterans)" icon="star" text="veterans" :sideBySide="true" />
              <ProfileDataPoint :dataPoint="formatThousandsNoDecimal(profileData.served.dv)" text="DV survivors" icon="dv" :sideBySide="true" />
            </ProfileDataRow>
          </div>

          <!-- CLOSED CASES -->
          <div class="grid-section-row">
            <ProfileDataRow title="Top Closed Cases" icon="gavel">
              <div class="grantee-radio" v-if="granteeDataPoints.length > 1">
                <div id="grantee-radio-header1" class="grantee-radio-header">Select a grantee</div>
                <v-radio-group class="grantee-radio-group" inline v-model="granteeRadio" label="" aria-labelledby="grantee-radio-header1">
                  <v-radio v-for="rno, index in granteeDataPoints" :key="rno.rno" :label="rno.data.org_name" :value="index" color="#163f62"></v-radio>
                </v-radio-group>
              </div>
              <div v-else class="single-grantee-name-header">
                {{ granteeDataPoints[0].data.org_name }}
              </div>
              <StateProfilesWebBarChart :caseData="granteeDataPoints[granteeRadio].cases" v-if="$vuetify.breakpoint.smAndDown === false" :key="granteeRadio + String(resizeWatch)" />
              <div v-else class="cases-bars-mobile-alt">
                <ProfileDataPoint v-for="pnt in granteeDataPoints[granteeRadio].cases" :key="pnt.name" :pointOnRight="true" :text="pnt.text" :italicText="pnt.italicText" :indentText="pnt.indentText" :dataPoint="pnt.dataPoint" :italicDataPoint="pnt.italicDataPoint" />
              </div>
            </ProfileDataRow>
          </div>

          <!-- FUNDING -->
          <div class="grid-section-row">
            <ProfileDataRow title="Funding" icon="dollar" columnsWhenWide="2">
              <div class="grantee-radio" v-if="granteeDataPoints.length > 1">
                <div id="grantee-radio-header2" class="grantee-radio-header">Select a grantee</div>
                <v-radio-group class="grantee-radio-group" inline v-model="granteeRadio" label="" aria-labelledby="grantee-radio-header2">
                  <v-radio v-for="rno, index in granteeDataPoints" :key="rno.rno" :label="rno.data.org_name" :value="index" color="#163f62"></v-radio>
                </v-radio-group>
              </div>
              <div v-else class="single-grantee-name-header">
                {{ granteeDataPoints[0].data.org_name }}
              </div>
              <ProfileDataPoint v-for="pnt in granteeDataPoints[granteeRadio].funding" icon="circle" :key="pnt.name" :dataPoint="String(pnt.dataPoint)" :text="pnt.text" :sideBySide="pnt.sideBySide"/>
            </ProfileDataRow>
          </div>
        </div>
      </div>

      <!-- SOURCES -->
      <div id="cd-profile-sources">
        <v-expansion-panels accordion>
          <v-expansion-panel>
            <v-expansion-panel-header><h4>Sources</h4></v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="sources-category">
                <div class="sources-category-name">Eligibility</div>
                <div class="sources-category-value">{{ acsYear }} American Community Survey 5-year estimates <span v-if="'pov_level_child_senior' in profileData.population && profileData.population.pov_level_child_senior === 100">. As of the latest update to this resource, ACS estimates do not reflect current congressional districts, so the estimates are calculated from all tracts fully or partially within the congressional district. The data points will be updated to ACS congressional district tabulations when available for current district boundaries.</span><span v-else>:</span>
                  <ul>
                    <li>Table S1701 for percent of the total population below 125% poverty.</li>
                    <li>Table <span v-if="'pov_level_child_senior' in profileData.population && profileData.population.pov_level_child_senior === 100">S1701</span><span v-else>S1703</span> for percent of children and seniors below <span v-if="'pov_level_child_senior' in profileData.population && profileData.population.pov_level_child_senior === 100">100</span><span v-else>125</span>% poverty.</li>
                    <li v-if="profileData.population.vet_pov_pct > 0.067">Table S2101 for percent of veterans below 100% poverty.</li>
                  </ul>
                </div>
              </div>
              <div class="sources-category">
                <div class="sources-category-name">Justice Gap</div>
                <div class="sources-category-value"><a href="https://justicegap.lsc.gov" target="_parent">2022 Justice Gap Study</a>, including the <a :href="'https://justicegap.lsc.gov/resource/' + profileData.justice_gap.region.toLowerCase()" target="_parent">Snapshot of Key Findings</a> for the district's region.
                </div>
              </div>
              <div class="sources-category">
                <div class="sources-category-name">Housing</div>
                <div class="sources-category-value">
                  <ul>
                    <li v-if="includesRentBurden">Percent of renters with rent burden (paying > 30% of income on rent) is calculated from {{ acsYear }} American Community Survey 5-year estimates, table B25070 (excluding "Not computed"<span v-if="'pov_level_child_senior' in profileData.population && profileData.population.pov_level_child_senior === 100">; calculated from relevant tracts -- see Eligibility above).</span><span v-else>).</span></li>
                    <li v-if="includesHrsPerWeek">Hours per week to afford a 1 bedrooom apartment in the state is from the National Low-Income Housing Coalition's {{ nlihcOorYear }} <a href="https://nlihc.org/oor" target="_parent">Out of Reach</a> report.</li>
                    <li v-if="includesELIUnits">The state's number of units available and affordable per 100 households with extremely low incomes is from the National Low-Income Housing Coalition's {{ nlihcGapYear }} <a href="https://nlihc.org/gap" target="_parent">The Gap</a> report.</li>
                  </ul>
                </div>
              </div>
              <div class="sources-category">
                <div class="sources-category-name">People served</div>
                <div class="sources-category-value">Reflects people served in handled cases (closed and open cases), from LSC's {{ profileData.gar_year }} Grantee Activity Reports. These estimates are calculated at the state level then proportioned to congressional districts based on the percent of the state's LSC eligible population that lives in the district. To provide a more complete picture of the work of LSC grantees, this methodology was updated in 2025 to reflect people served in cases handled instead of cases closed only. LSC grantees also reach millions of people through community legal education and outreach that are not reflected in these numbers.
                  <ul>
                    <li>People served in handled cases is calculated as the number of handled cases multiplied by the national average household size of closed cases.</li>
                    <li>Veterans served in handled cases is calculated as the number of cases closed with a veteran in the household multiplied by the national ratio of handled to closed cases.</li>
                    <li>Older Americans served in handled cases is calculated as the number of clients 60 and over in closed cases multiplied by the national average number of adults per closed case and the national ratio of handled to closed cases.</li>
                    <li>Domestic violence survivors served in handled cases is calculated as the number of closed cases involving domestic violence multiplied by the national ratio of handled to closed cases.</li>
                  </ul>
                </div>
              </div>
              <div class="sources-category">
                <div class="sources-category-name">Cases closed</div>
                <div class="sources-category-value">From LSC's {{ profileData.gar_year }} Grantee Activity Reports. Legal problem categories are not displayed where the number of closed cases was less than 2% of the total, or for miscellaneous categories (e.g., "Other Housing").
                </div>
              </div>
              <div class="sources-category">
                <div class="sources-category-name">Funding</div>
                <div class="sources-category-value">LSC grant awards, 2020 to present, plus total revenue from {{ profileData.gar_year }} Grantee Activity Reports.
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </Block>
  </div>
</template>

<script>
import Block from '@/components/layout/Block'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import ProfileDataRow from '@/components/st_cd_snapshots/ProfileDataRow'
import ProfileDataPoint from '@/components/st_cd_snapshots/ProfileDataPoint'
import StateProfilesWebBarChart from '@/views/advocacy/congress/state_profiles/StateProfilesWebBarChart'
import CDProfilesPrintStateMap from '@/views/advocacy/congress/cd_profiles/CDProfilesPrintStateMap'
import CDProfilesWebMap from '@/views/advocacy/congress/cd_profiles/CDProfilesWebMap'
import { unique_colors } from '@/js/map_utils.js'
import { mdiCircle, mdiAccountMultiple, mdiStar, mdiFileDownloadOutline } from '@mdi/js';
import { problemCodes, categoryRename, codesPerCategory } from '@/js/problem_codes.js'
import { formatThousandsNoDecimal, percentToOneInX, formatPercentNoDecimalIfExists } from "@/js/number_formatters.js";
import grantees from '@/js/grantees.js'
import snapshotLinks from '@/js/snapshots_box_links.js'

export default {
  name: 'CDProfilesWeb',

  components: { Block, ProfileDataRow, ProfileDataPoint, LoadingSpinner, StateProfilesWebBarChart, CDProfilesPrintStateMap, CDProfilesWebMap },

  props: {
    profileData: { type: Object, required: true },
    selectedCDFips: { type: String, required: true },
    selectedCDLabel: { type: String, required: true },
    granteesByType: { type: Object, required: true },
    officesPerGrantee: { type: Object, required: true },
    granteesInState: { type: Array, required: true }
  },

  data () {
    return {
      loading: true,
      hideStateMap: [ '1502' ], // For this list of districts, the smaller state map is redundant with the map zoomed in to the district
      resizeWatch: 0,
      debounceTimer: 0,
      acsYear: null,
      nlihcOorYear: null,
      nlihcGapYear: null,
      icons: { 
        circle: mdiCircle, 
        serves: mdiAccountMultiple, 
        main: mdiStar,
        download: mdiFileDownloadOutline
      },
      housingPoints: [],
      granteeDataPoints: [],
      granteeRadio: 0,
      colors: unique_colors,
      officesShown: null,
      hasOfficeInDistrict: false,
      includesRentBurden: false,
      includesHrsPerWeek: false,
      includesELIUnits: false,
      grantees: grantees,
      onePagerBoxLinksByState: snapshotLinks
    }
  },

  computed: {
    colorPerServArea () {
      let areas = []
      let c = this.colors
      let usedColors = {}
      for (let i = 0; i < this.granteesByType.bfGeneral.length; i++) {
      let a = this.granteesByType.bfGeneral[i]
      if (Object.keys(this.profileData.grantees).indexOf(a.recipient_id) > -1) { // Filter out any grantees that rounded to serving 0.0% of constituents
        if (!usedColors[a.recipient_id]) {
        usedColors[a.recipient_id] = c[i]
        }
        areas.push({ area: a.serv_area, color: usedColors[a.recipient_id], rno: a.recipient_id })
      }
      }
      return areas
    },
    singleGranteeState () {
      return this.granteesInState.filter(x => x.serv_area_type === 'Basic Field').length === 1
    },
    singleCDState () {
      return this.selectedCDFips.substring(2,4) === '00' || this.selectedCDFips.substring(2,4) === '98'
    },
    numGrantees () {
      return Object.keys(this.profileData.grantees).length
    }
  },

  created () {
    this.parseProfileData()
  },

  mounted () {
    this.initObserver()
  },
  
  beforeDestroy() {
    // Unobserve resize
    if (this.resizeObserver) this.resizeObserver.unobserve(this.$refs.profilearea)
  },  
  
  methods: {
    async parseProfileData () {
      this.loading = true
      this.acsYear = this.profileData.population.acs_year
      this.setHousingPoints(this.profileData)
      this.setGranteeDataPoints(this.profileData)
      this.loading = false
    },

    setHousingPoints (d) {
      let points = []

      // Percent rental cost burdened
      if (d.housing.pct_rent_burdened !== null) {
        points.push(
          { name: 'Rental cost burdened', dataPoint: `${formatPercentNoDecimalIfExists(d.housing.pct_rent_burdened)}`, text: 'of renter households pay more than 30% of income on rent.', inlinePoint: true }
        )
        this.includesRentBurden = true
      }

      // Work hours to afford 1 bedroom at FMR -- if above 65 hours
      if (d.housing.nlihc !== null && "hrs_wk_for_1br_fmr" in d.housing.nlihc && d.housing.nlihc.hrs_wk_for_1br_fmr !== null && d.housing.nlihc.hrs_wk_for_1br_fmr > 65) {
        points.push(
          { name: 'NLIHC hours of work', dataPoint: `${String(d.housing.nlihc.hrs_wk_for_1br_fmr)} hours/week`, text: `at minimum wage to afford a 1-bedroom unit at Fair Market Rent in ${d.state}.`, inlinePoint: true }
        )
        this.includesHrsPerWeek = true
        this.nlihcOorYear = d.housing.nlihc.oor_report_year
      }

      // Units per ELI renter HH
      if (d.housing.nlihc !== null && "units_per_eli" in d.housing.nlihc && d.housing.nlihc.units_per_eli !== null && d.housing.nlihc.units_per_eli < 65) {
        points.push(
          { name: 'NLIHC units per ELI', dataPoint: `Only ${String(d.housing.nlihc.units_per_eli)}`,text: "affordable units are available for every 100 extremely low-income renter households.", inlinePoint: true }
        )
        this.includesELIUnits = true
        this.nlihcGapYear = d.housing.nlihc.gap_report_year
      }
      
      this.housingPoints = points
      return 
    },

    setGranteeDataPoints(d) {
      let granteesList = []
      for (let rno of Object.keys(d.grantees)) {
        granteesList.push({ rno: rno, data: d.grantees[rno], cases: this.setCasesPoints(d.grantees[rno]), funding: this.setFundingPoints(d.grantees[rno], d) })
      }
      this.granteeDataPoints = granteesList.sort((a,b) => b.data.percent_of_constituents - a.data.percent_of_constituents )
    },

    setCasesPoints (d) {
      /**
       * Creates the list of problem categories and percentages.
       */

      let points = []

      // Convert categories and codes to new array of objects for sorting 
      let categories = this.keyValuesToSortedArrayOfObjects(d.closed_cases.categories)
      let codes = this.keyValuesToSortedArrayOfObjects(d.closed_cases.codes_over_10_pct)
    
      // Exclude the "other_" codes from each category
      const codesToExclude = ['09', '19', '29', '39', '49', '59', '69', '79', '89', '99']
      const miscCodes = ['90','91','92','93','94','95','96','97','98']

      // Treat problem codes under "Miscellaneous" as if they were problem categories
      for (let x of codes) {
        if (miscCodes.indexOf(x.key) > -1) {
          categories.push(x)
        }
      }

      categories.sort((a, b) => parseFloat(b.value) - parseFloat(a.value));

      // Add each problem category (and its sub-codes) in order for the list of data points
      for (const c of categories) {
        if (c.value > 0.02 && categoryRename[c.key] !== 'Miscellaneous' && categoryRename[c.key] !== 'Misc') {
          let name = miscCodes.indexOf(c.key) > -1 ? problemCodes.filter(x => x.code === parseInt(c.key))[0].shortName : categoryRename[c.key]

          points.push(
            { name: `${c.key} cases`, text: name, dataPoint: `${formatPercentNoDecimalIfExists(c.value)}`,  italicText: false, indentText: false, italicDataPoint: false, origData: c.value }
          )
        }

        // Show any codes within this category if > 10% of all cases closed
        if (miscCodes.indexOf(c.key) > -1 || c.key === 'misc') { continue }

        const codesForThisCategory = codes.filter(x => codesPerCategory[c.key].indexOf(parseInt(x.key)) > -1)
        
        if (codesForThisCategory.length > 0) {
          for (const co of codesForThisCategory) {
            if (co.value > 0.1 && codesToExclude.indexOf(co.key) === -1) {
              points.push(
                { name: `${co.key} cases`, text: problemCodes.filter(x => x.code === parseInt(co.key))[0].shortName, dataPoint: `${formatPercentNoDecimalIfExists(co.value)}`, italicText: true, indentText: true, italicDataPoint: true, origData: co.value }
              )
            }
          }
        }
      }
      return points
    },


    setFundingPoints (d, overall_d) {
      let points = []

      const bfVars = {'bf_general': 'to serve the eligible population', 'ag_worker': 'to serve agricultural workers', 'native_am': 'to serve Native Americans'}

      // Total revenue
      if (d.funding.total_revenue !== null && d.funding.total_revenue > 0) {
          points.push(
            { name: `total revenue`, dataPoint: `$${formatThousandsNoDecimal(d.funding.total_revenue)}`, text: `total revenue in ${overall_d.gar_year} (LSC and non-LSC)`, sideBySide: true }
          )
        }

      // BF grants
      for (const x of Object.keys(bfVars)) {
        if (x in d.funding.basic_field && d.funding.basic_field[x] !== null && d.funding.basic_field[x] > 0) {
          points.push(
            { name: `${x} funding`, dataPoint: `$${formatThousandsNoDecimal(d.funding.basic_field[x])}`, text: `in ${d.funding.funding_year} ${bfVars[x]}`, sideBySide: true }
          )
        }        
      }

      // Then special grants
      const sgVars = {'disaster': 'to respond to disasters', 'pbif': 'for pro bono innovation', 'tig': 'for technology innovation'}

      for (const x of Object.keys(sgVars)) {
        if (x in d.funding.special_grants && d.funding.special_grants[x] !== null && d.funding.special_grants[x] > 0) {
          points.push(
            { name: `${x} funding`, dataPoint: `$${formatThousandsNoDecimal(d.funding.special_grants[x])}`, text: `since ${d.funding.special_grants.since_year} ${sgVars[x]}`, sideBySide: true }
          )
        }        
      }

      return points
    },


    keyValuesToSortedArrayOfObjects(obj) {
      /**
       * Convert an object like { key1: value1, key2: value2 } to an array of objects 
       * like [{ key: key1, value: value1 }, { key: key2, value: value2 }]
       * @param {object}  obj Object like { key1: value1, key2: value2 }
       * @return {array}  Returns array of objects
       */
      return Object.entries(obj).map(([key, value]) => ({key, value})).sort((a, b) => b.value - a.value)
    },

    formatPercentNoDecimalIfExists (val) { return formatPercentNoDecimalIfExists(val) },
    percentToOneInX (val) { return percentToOneInX(val) },
    formatThousandsNoDecimal (val) { return formatThousandsNoDecimal(val) },

    selectedOrgProfileLink (rno) {
      let node = this.grantees.filter(x => x.value === rno)[0].lscDotGovProfileNode
      return `https://www.lsc.gov/node/${node}`
    },

    parseOfficesList(e) {
      let o = {}
      for (let g of Object.keys(e)) {
        let citiesUnique = []
        let citiesWithCount = []
        for (let i = 0; i < e[g].length; i++) {
          if (e[g][i].properties.inCD === "true") { this.hasOfficeInDistrict = true }
          let city = e[g][i].properties.city
          if (citiesUnique.indexOf(city) === -1) {
            citiesUnique.push(city)
            let countOfCity = e[g].map(x => x.properties.city).filter(x => x === city).length
            let inCDAsterisk = e[g].filter(x => x.properties.city === city && x.properties.inCD === 'true').length > 0 && this.singleCDState === false ? '*' : ''
            if (countOfCity === 1) {
              citiesWithCount.push(`${inCDAsterisk}${city}`)
            } else if (countOfCity > 1) {
              citiesWithCount.push(`${inCDAsterisk}${city} (x${countOfCity})`)
            }
          }
        }
        o[g] = citiesWithCount.sort().join(', ')
      }
      this.officesShown = o
    },

    initObserver() {
      const observer = new ResizeObserver(this.onResize)
      observer.observe(this.$refs.profilearea)
      this.resizeObserver = observer
    },
    onResize() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {this.resizeWatch +=1}, 200);
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/scss/vars.scss';
$spacer-height: 5rem;

#cd-profile-area-web {
  padding: 0;
  display: grid;
  grid-template-areas:
    'header header header'
    'download download download'
    'needs needs needs'
    'spacer spacer spacer'
    'granteesheader granteesheader granteesheader'
    'grantees map map'
    'spacer2 spacer2 spacer2'
    'served served served';
  gap: 1rem;
  grid-auto-columns: 1fr;
  margin: 0 auto;

  .grid-header {
    grid-area: header;
    display: flex;
    justify-content: center;

    .main-title {
      display: flex;
      flex-direction: column;
      justify-content: flex-start; 
      text-align: center;
      line-height: 2rem;
      padding: 0;

      .title-1 {
        color: #404040;
        letter-spacing: 0.2rem;
        font-family: 'Lato', sans-serif;
        font-size: 1.25rem;
        font-weight: normal;
        margin-bottom: 0.5rem;
      }
      .title-2 {
        font-size: 4rem;
        line-height: normal;
        margin-bottom: 0;
      }
    }
  }
  .grid-download {
    grid-area: download;
    margin-bottom: 0.5rem;
    text-align: center;
  }
  .grid-needs {
    grid-area: needs;
  }
  .grid-map {
    grid-area: map;
    width: 100%;
  }
  .grid-grantees-header {
    grid-area: granteesheader;
    display: flex;
    justify-content: space-between;
    align-items: center;

    #st-map {
        width: 25rem;
      }
  }
  .grid-grantees {
    grid-area: grantees;

    #grantees-list {
      height: 100%;
      display: flex;
      flex-direction: column;
      margin-top: 2rem;
      justify-content: flex-start;

      .grantees-list-g {
        padding: 0.5rem 0 2rem 0;
      }
      .grantee-name-row {
        display: flex;
      }
      .grantee-list-item-header {
        padding: 0.25rem 2rem 0.25rem 0.5rem;
        margin: 0;
        display: flex;
        align-items: center;
        cursor: pointer;
        border-radius: 5px;
        min-height: 1.25rem;
        line-height: 1.5;
        background-color: transparent;
        font-size: 1rem;
        transition: all 0.1s ease-in-out;
        clip-path: polygon(0 0, calc(100% - 1rem) 0, 100% 50%, 100% 50%, calc(100% - 1rem) 100%, 0 100%);

        &:hover {
          border: 0;
          cursor: pointer;
          font-weight: 700;
          text-decoration: none !important;
          transition: 0.2s ease-out;
          background-color: #b10000;
          color: #fff !important;
        }
      }
      .grantee-color {
        margin-right: 0.5rem;
      }
      .grantees-list-info {
        display: flex;
        flex-direction: column;
        margin: 0.25rem 0 0.5rem 1.5rem;
        font-weight: 400;
        color: #5a5a5a;
        font-size: 1rem;

        >div {
          padding: 0.5rem 0;
        }

        span.grantee-info-point {
          color: #b31308;
          font-weight: bold;
        }
      }
      .grantee-count-1 .grantees-list-info {
        margin-top: 1rem;
        margin-bottom: 1rem;
      }
      .grantee-count-1 .grantees-list-info {
        margin-top: 1rem;
        margin-bottom: 1rem;
      }
      .grantees-list-icon {
        margin-right: 0.45rem;
      }
    }
  }
  .grid-served {
    grid-area: served;
  }
  .grid-needs, .grid-served {
    padding: 1rem;
    border-radius: 0.625rem;
    background-color: #daeefd;
  }
  .grid-spacer {
    grid-area: spacer;
    height: 1rem;

    &.spacer-height-1 {
      height: $spacer-height;
    }
  }
  .grid-spacer2 {
    grid-area: spacer2;
    height: $spacer-height;
  }
  .grid-section-row {
    margin-bottom: 1rem;
  }
  .grid-section h3 {
    margin: 1rem 0 2rem 1rem;
  }
}
#cd-profile-sources {
  margin-top: 5rem;

  .v-expansion-panels {
    border: 1px solid #e0eefa;
    border-radius: 0.5rem;
    margin-bottom: 1rem;
    background-color: #f0f7fc;
  }
  .v-expansion-panel {
    background-color: inherit;
  }
  .v-expansion-panel::before {
    box-shadow: none;
  }
  .v-expansion-panel-header {
    color: $header-red;
    letter-spacing: 0.2rem;
    font-weight: 400;
  }
  .sources-category {
    display: flex;
    font-size: 0.95rem;
    margin-bottom: 1rem;
    flex-wrap: nowrap;
    
    .sources-category-name {
      width: 8rem;
      min-width: 8rem;
      color: $main-blue;
      padding-right: 2rem;
    }
  }
}
#cd-profile-area-web {
  &.mobile {
    .main-title {
      .title-1 {
        font-size: 1rem;
      }
      .title-2 {
        font-size: 2rem;
      }
    }
  }
  .single-grantee-name-header {
    font-size: 0.9rem;
    padding-left: 1rem;
    padding-top: 0.75rem;
    color: #5a5a5a;
  }
  .grantee-radio {
    display: flex;

    .v-label {
      color: #5a5a5a;
    }
    .grantee-radio-header {
      font-size: 0.9rem;
      padding: 1.25rem 0 0.5rem 1rem;
      margin-right: 1rem;
      color: #5a5a5a;
      font-style: italic;
      min-width: 7.5rem;
    }
    .v-input--radio-group--column .v-input--radio-group__input {
      flex-direction: row;
      align-items: baseline;
      flex-wrap: wrap;
    }
    .v-radio {
      margin-right: 1rem;
    }
  }
  .cases-bars-mobile-alt {
    max-width: 35rem;
  }
}
@media screen and (max-width: 800px) {
  #cd-profile-area-web {
      padding: 0 0.125rem;
  }
  #cd-profile-sources .sources-category {
    flex-wrap: wrap;
  }
}
@media screen and (max-width: 1000px) {
  #cd-profile-area-web {
    .grid-section {
      padding-left: 0.25rem;
      padding-right: 0.125rem;
    }
    grid-template-areas:
      'header'
      'download'
      'needs'
      'spacer'
      'granteesheader'
      'grantees'
      'map'
      'spacer2'
      'served';
  }
}
</style>